import React, {Component} from 'react';
import PropTypes from 'prop-types';
import clsx from "clsx";
import {Field} from "redux-form";
import {delve} from "../../util/util";

class LabeledSliderComponent extends Component {

    constructor(props) {
        super(props);

        this.handleRef = React.createRef();
        this.trackRef = React.createRef();

        const initiallySelectedIndex = Math.max(0, this.props.options.findIndex(option => {
            return option.value === this.props.input.value;
        }));

        this.state = {
            dragStartClientX: 0,
            dragStart: 0,
            dragPercentage: this.getDragPercentageByIndex(initiallySelectedIndex),
            selectedIndex: initiallySelectedIndex,
            isDragging: false,
            canTriggerNegativeRepeat: false,
            negativeRepeatCount: 0,
        }

        this.changeTimeout = 0;

        this.handleDragEnd = this.handleDragEnd.bind(this);
        this.handleDragStart = this.handleDragStart.bind(this);
        this.handleDragMove = this.handleDragMove.bind(this);
        this.handleTrackPress = this.handleTrackPress.bind(this);
    }

    componentWillUnmount() {
        document.removeEventListener("mousemove", this.handleDragMove);
        document.removeEventListener("mouseup", this.handleDragEnd);
        document.removeEventListener("mouseleave", this.handleDragEnd);
        document.removeEventListener("touchmove", this.handleDragMove);
        document.removeEventListener("touchend", this.handleDragEnd);
        if (this.trackRef && this.trackRef.current) {
            this.trackRef.current.removeEventListener("mousedown", this.handleTrackPress);
            this.trackRef.current.removeEventListener("touchstart", this.handleTrackPress);
        }
        if (this.handleRef && this.handleRef.current) {
            this.handleRef.current.removeEventListener("mousedown", this.handleTrackPress);
            this.handleRef.current.removeEventListener("touchstart", this.handleTrackPress);
        }
        if (this.changeTimeout) {
            this.triggerChange(this.state.selectedIndex);
            clearTimeout(this.changeTimeout);
            this.changeTimeout = 0;
        }
    }

    getDragPercentageByIndex(index) {
        return (index) / (this.props.options.length - 1);
    }

    getIndexByDragPercentage(percentage) {
        // Assume even spread, round and -1 := nearest to cursor
        const amountValues = this.props.options.length;
        return Math.max(0, Math.min(amountValues, Math.round(percentage * (amountValues - 1))))
        //return Math.max(0, Math.min(amountValues - 1, Math.floor(percentage * (amountValues))))
    }

    triggerChange(previouslySelectedIndex) {
        this.props.input.onChange(
            this.props.options[this.state.selectedIndex].value
        );
        if (this.props.onMaximumSelected && this.state.selectedIndex === this.props.options.length - 1 && this.state.selectedIndex > 0) {
            this.props.onMaximumSelected();
        }
        if (
            this.props.onMaximumDeselected
            && previouslySelectedIndex > 0
            && this.state.selectedIndex !== this.props.options.length - 1
            && previouslySelectedIndex !== this.state.selectedIndex
        ) {
            this.props.onMaximumDeselected();
        }
    }

    updateDragPercentage(percentage) {
        const selectedIndex = this.getIndexByDragPercentage(percentage);
        const currentlySelectedIndex = this.state.selectedIndex;

        if (currentlySelectedIndex !== selectedIndex) {
            // set index before calling input.onChange, else componentDidUpdate has wrong state
            this.setState({
                selectedIndex
            });

            if (this.changeTimeout) {
                clearTimeout(this.changeTimeout);
                this.changeTimeout = 0;
            }

            if (!this.props.disabled) {
                this.changeTimeout = setTimeout(() => {
                    this.triggerChange(currentlySelectedIndex);
                }, 50);
            }

        }
    }

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        if (nextState.isDragging !== this.state.isDragging) {
            return true;
        }
        if (nextState.selectedIndex !== this.state.selectedIndex) {
            return true;
        }
        if (nextProps.disabled !== this.props.disabled) {
            return true;
        }
        if (nextState.dragPercentage !== this.state.dragPercentage) {
            return true;
        }
        if (nextProps.options.length !== this.props.options.length || nextProps.options[0].value !== this.props.options[0].value) {
            return true;
        }
        if (JSON.stringify(nextProps.options) !== JSON.stringify(this.options)) {
            return true;
        }
        if (nextProps.input.value !== this.props.input.value) {
            return true;
        }
        return false;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {

        if (
            (prevProps.options.length !== this.props.options.length)
            ||
            (JSON.stringify(prevProps.options) !== JSON.stringify(this.props.options))
        ) {
            // set drag percentage when value changes without dragging
            const selectedIndexUpdate = Math.max(0, this.props.options.findIndex(option => {
                return option.value === this.props.input.value;
            }));

            this.setState({
                selectedIndex: selectedIndexUpdate,
                dragPercentage: this.getDragPercentageByIndex(selectedIndexUpdate)
            });

            this.initEventListeners();
        }

        if (prevProps.input.value !== this.props.input.value) {
            const selectedIndexUpdate = Math.max(0, this.props.options.findIndex(option => {
                return option.value === this.props.input.value;
            }));
            if (this.state.selectedIndex !== selectedIndexUpdate && !this.state.isDragging) {
                // value changed, drag percentage needs to be updated, only when not dragging manually
                this.setState({
                    selectedIndex: selectedIndexUpdate,
                    dragPercentage: this.getDragPercentageByIndex(selectedIndexUpdate)
                });
            }
        }

        if (prevProps.disabled && !this.props.disabled) {
            // when api is ready, check if value is up to date
            if (this.props.options[this.state.selectedIndex].value !== this.props.input.value) {
                if (this.changeTimeout) {
                    clearTimeout(this.changeTimeout);
                    this.changeTimeout = 0;
                }
                this.changeTimeout = setTimeout(() => {
                    this.triggerChange(prevState.selectedIndex);
                }, 350);
            }
        }

        if (!prevProps.disabled && this.props.disabled) {
            if (this.changeTimeout) {
                clearTimeout(this.changeTimeout);
                this.changeTimeout = 0;
            }
        }
    }

    handleDragMove(e) {

        const dragTrackWidth = this.trackRef.current.getBoundingClientRect().width;
        const x = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);

        if (!x) {
            // x undefined on window border
            return;
        }

        // limit dragCurrent between 0 and width - 24
        const dragCurrent = Math.max(0, Math.min(dragTrackWidth - 24,
            this.state.dragStart + (x - this.state.dragStartClientX)
        ));

        const dragPercentage = dragCurrent / (dragTrackWidth - 24);

        if (Math.min(dragTrackWidth - 24, this.state.dragStart + (x - this.state.dragStartClientX)) < 0) {
            if (this.state.canTriggerNegativeRepeat) {
                this.setState({
                    canTriggerNegativeRepeat: false,
                    negativeRepeatCount: this.state.negativeRepeatCount + 1
                })
                setTimeout(() => {
                    this.setState({
                        negativeRepeatCount: Math.max(0, this.state.negativeRepeatCount - 1),
                    });
                }, 5000)
                if (this.state.negativeRepeatCount === 3 && this.props.onRepeatableNegativeDrag) {
                    this.props.onRepeatableNegativeDrag.call();
                    this.setState({
                        negativeRepeatTriggered: true,
                    });
                }
            }
        } else {
            this.setState({
                canTriggerNegativeRepeat: true
            });
            if (this.getIndexByDragPercentage(dragPercentage) > 0 && this.state.negativeRepeatTriggered && this.props.onClearPositiveDrag) {
                this.setState({
                    negativeRepeatTriggered: false,
                    negativeRepeatCount: 0,
                });
                this.props.onClearPositiveDrag.call();
            }
        }

        this.setState({
            dragPercentage
        })

        this.updateDragPercentage(dragPercentage);

        e.stopPropagation();
        e.preventDefault();
    }

    handleDragEnd(e) {

        document.removeEventListener("mousemove", this.handleDragMove);
        document.removeEventListener("mouseup", this.handleDragEnd);
        document.removeEventListener("mouseleave", this.handleDragEnd);

        document.removeEventListener("touchmove", this.handleDragMove);
        document.removeEventListener("touchend", this.handleDragEnd);

        const dragPercentage = this.getDragPercentageByIndex(this.state.selectedIndex);

        this.setState({
            isDragging: false,
            dragPercentage
        })
    }

    handleDragStart(e) {

        const dragTrackWidth = this.trackRef.current.getBoundingClientRect().width;
        const x = e.clientX || (e.touches && e.touches[0] && e.touches[0].pageX);

        document.addEventListener("mousemove", this.handleDragMove);
        document.addEventListener("mouseup", this.handleDragEnd);
        document.addEventListener("mouseleave", this.handleDragEnd);

        document.addEventListener("touchmove", this.handleDragMove, {passive: false});
        document.addEventListener("touchend", this.handleDragEnd, {passive: false});

        this.setState({
            dragStartClientX: x,
            dragStart: this.state.dragPercentage * (dragTrackWidth - 24),
            isDragging: true,
            canTriggerNegativeRepeat: true
        })

        e.stopPropagation();
        e.preventDefault();
    }

    handleTrackPress(e) {

        const dragTrackLeft = this.trackRef.current.getBoundingClientRect().left;
        const dragTrackWidth = this.trackRef.current.getBoundingClientRect().width;
        const x = e.clientX || (e.touches && e.touches[0] && e.touches[0].pageX);

        const dragCurrent = Math.max(0, Math.min(dragTrackWidth - 24, x - dragTrackLeft - 12));
        const dragPercentage = dragCurrent / (dragTrackWidth - 24);

        this.updateDragPercentage(dragPercentage);

        document.addEventListener("mousemove", this.handleDragMove);
        document.addEventListener("mouseup", this.handleDragEnd);
        document.addEventListener("mouseleave", this.handleDragEnd);

        document.addEventListener("touchmove", this.handleDragMove, {passive: false});
        document.addEventListener("touchend", this.handleDragEnd, {passive: false});

        this.setState({
            dragStartClientX: x,
            dragStart: x - dragTrackLeft - 12,
            dragPercentage,
            isDragging: true
        })


        e.stopPropagation();
        e.preventDefault();
    }

    initEventListeners() {
        if (this.trackRef.current) {
            this.trackRef.current.removeEventListener("mousedown", this.handleTrackPress);
            this.trackRef.current.addEventListener("mousedown", this.handleTrackPress);
            this.trackRef.current.removeEventListener("touchstart", this.handleTrackPress);
            this.trackRef.current.addEventListener("touchstart", this.handleTrackPress, {passive: false});
        }
        if (this.handleRef.current) {
            this.handleRef.current.removeEventListener("mousedown", this.handleTrackPress);
            this.handleRef.current.addEventListener("mousedown", this.handleDragStart);
            this.handleRef.current.removeEventListener("touchstart", this.handleTrackPress);
            this.handleRef.current.addEventListener("touchstart", this.handleDragStart, {passive: false});
        }
    }

    componentDidMount() {
        this.initEventListeners();
    }

    selectIndex(targetIndex) {
        this.setState({
            selectedIndex: targetIndex,
            dragPercentage: this.getDragPercentageByIndex(targetIndex)
        });
    }

    handleOnKeyDown = (e) => {

        const currentlySelectedIndex = this.state.selectedIndex;


        if (this.changeTimeout) {
            clearTimeout(this.changeTimeout);
            this.changeTimeout = 0;
        }

        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault();
            let targetIndex = (this.state.selectedIndex - 1) < 0 ? 0 : this.state.selectedIndex - 1;
            this.selectIndex(targetIndex)
            this.changeTimeout = setTimeout(() => {
                this.triggerChange(currentlySelectedIndex)
            }, 500);
        }
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault();
            let targetIndex = (this.state.selectedIndex + 1) > (this.props.options.length - 1) ? this.props.options.length - 1 : this.state.selectedIndex + 1;
            this.selectIndex(targetIndex)
            this.changeTimeout = setTimeout(() => {
                this.triggerChange(currentlySelectedIndex)
            }, 500);
        }
        if (e.key === "Home") {
            e.preventDefault();
            this.selectIndex(0);
            this.changeTimeout = setTimeout(() => {
                this.triggerChange(currentlySelectedIndex)
            }, 500);
        }
        if (e.key === "End") {
            e.preventDefault();
            this.selectIndex(this.props.options.length - 1);
            this.changeTimeout = setTimeout(() => {
                this.triggerChange(currentlySelectedIndex)
            }, 500);
        }
    }

    render() {


        const targetPercentage = this.getDragPercentageByIndex(this.state.selectedIndex);

        const selectedOptionLabel = delve(this.props.options, this.state.selectedIndex + ".label");
        const selectedOptionDisabled = delve(this.props.options, this.state.selectedIndex + ".disabled");
        const selectedOptionDisabledSuffix = delve(this.props.options, this.state.selectedIndex + ".disabledSuffix");

        let markerAtOptionIndex = ((this.props.markerAtIndex === 0 || this.props.markerAtIndex > 0) ? this.props.markerAtIndex : -1);

        if (this.props.markerAtOption) {
            markerAtOptionIndex = this.props.options.findIndex((element) => element.value === '' + this.props.markerAtOption);
        }

        if (this.props.options && this.props.options.length === 1) {
            return <div
                className={clsx("labeled-slider labeled-slider--single", this.props.layout && ("labeled-slider--layout-" + this.props.layout))}>
                <div className={"labeled-slider__track__label"}>
                    {selectedOptionLabel}
                    {selectedOptionDisabled ?
                        <span className={"labeled-slider__label__error"}>{selectedOptionDisabledSuffix}</span> : null}
                </div>
            </div>;
        }

        let inlineDisplayTheme = false;

        if (this.props.inlineDisplayPercent) {
            inlineDisplayTheme = 'green'
            if (this.props.inlineDisplayPercent >= 100) {
                inlineDisplayTheme = 'red'
            } else if (this.props.inlineDisplayPercent > 90) {
                inlineDisplayTheme = 'orange'
            }
        }

        return (
            <div
                tabIndex={0}
                role={"slider"}
                aria-valuemax={this.props.options[this.props.options.length - 1].label}
                aria-valuemin={this.props.options[0].label}
                aria-valuenow={selectedOptionLabel}
                onKeyDown={this.handleOnKeyDown}
                className={clsx(
                    "labeled-slider",
                    this.state.isDragging && "dragging",
                    this.props.layout && ("labeled-slider--layout-" + this.props.layout),
                    this.props.inlineDisplay && 'labeled-slider--inline-display'
                )}
            >
                {this.props.inlineDisplay ?
                    <div
                        className={clsx("labeled-slider__inline-display", inlineDisplayTheme && ("labeled-slider__inline-display--theme-" + inlineDisplayTheme))}
                    >
                        <div
                            className={"labeled-slider__inline-display__label"}
                        >
                            {this.props.inlineDisplayLabel}
                        </div>
                        <div
                            className={"labeled-slider__inline-display__active"}
                            style={{width: (this.props.inlineDisplayPercent) + "%"}}
                        />
                        <div
                            className={"labeled-slider__inline-display__background"}
                        />
                    </div> : null}
                <div
                    ref={this.trackRef}
                    className={clsx("labeled-slider__track", this.state.isDragging && "dragging")}
                >
                    <div
                        className={"labeled-slider__track__label"}
                        style={
                            {
                                left: `calc(${targetPercentage} * 100%)`,
                                transform: `translateX(calc(-1 * ${targetPercentage} * 100%))`
                            }
                        }
                    >
                        {selectedOptionLabel}
                        {selectedOptionDisabled ? <span
                            className={"labeled-slider__label__error"}>{selectedOptionDisabledSuffix}</span> : null}
                    </div>
                    <div
                        className={clsx("labeled-slider__track__handle", this.state.isDragging && "dragging")}
                        style={{left: `calc(${targetPercentage} * (100% - 24px))`}}
                        ref={this.handleRef}
                    />
                    <div
                        className={"labeled-slider__track__active"}
                        style={{width: `calc(${targetPercentage} * (100% - 24px) + 12px)`}}
                    />
                    {(markerAtOptionIndex === 0 || markerAtOptionIndex > 0) ? <div
                        className={"labeled-slider__track__marker"}
                        style={{left: `calc(${this.getDragPercentageByIndex(markerAtOptionIndex)} * (100% - 24px))`}}
                    /> : null}
                    <div
                        className={"labeled-slider__track__exceeding"}
                        style={{width: `calc(${this.props.exceedingSteps || 0} * (100% - 24px))`}}
                    >
                    </div>
                    <div className={"labeled-slider__track__steps"}>
                        {this.props.options.map((option, index) => {
                            return index > 0 ? <div
                                key={`lbl_sld_stp_${index}`}
                                className={clsx((index) === this.state.selectedIndex && "active")}
                            /> : null;
                        })}
                    </div>
                    <div className={"labeled-slider__track__background"}/>
                </div>
            </div>
        );

    }
}

function LabeledSlider(props) {
    return <Field
        name={props.fieldId}
        forceValidation={props.forceValidation}
        component={LabeledSliderComponent}
        props={props}
    />
}

LabeledSlider.propTypes = {
    options: PropTypes.array.isRequired,
    inlineDisplay: PropTypes.bool,
    inlineDisplayLabel: PropTypes.string,
    inlineDisplayPercent: PropTypes.number,
    exceedingSteps: PropTypes.any,
    inlineDisplayTheme: PropTypes.string,
    markerAtOption: PropTypes.any,
    markerAtIndex: PropTypes.number,
    fieldId: PropTypes.string.isRequired,
    onMaximumSelected: PropTypes.func,
    onRepeatableNegativeDrag: PropTypes.func,
    onMaximumDeselected: PropTypes.func
}

export default LabeledSlider;



/** WEBPACK FOOTER **
 ** ./src/components/forms/LabeledSlider.js
 **/